import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import * as saIdParser from 'south-african-id-parser';

export class NameValidators {
  static noIdenticalNames(nameKey: string, lastnameKey: string): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const nameControl = group.get(nameKey);
      const lastnameControl = group.get(lastnameKey);

      if (!nameControl || !lastnameControl) {
        return null;
      }

      const name = nameControl.value;
      const lastname = lastnameControl.value;

      // Get current errors for lastName
      const currentErrors = lastnameControl.errors;

      if (name === lastname) {
        // Merge the 'identicalNames' error with existing errors
        lastnameControl.setErrors({
          ...currentErrors,
          identicalNames: true,
        });
      } else {
        // Remove 'identicalNames' error while keeping others
        if (currentErrors) {
          const { identicalNames, ...otherErrors } = currentErrors;
          lastnameControl.setErrors(Object.keys(otherErrors).length > 0 ? otherErrors : null);
        }
      }

      return null; // No form-level error needed
    };
  }

  static noSingleCharacter(control: AbstractControl): ValidationErrors | null {
    return control.value && control.value.length === 1 ? { singleCharacter: true } : null;
  }

  static noConsecutiveRepeats(control: AbstractControl): ValidationErrors | null {
    const value = control.value as string;
    return /(.)\1{2,}/.test(value.toLowerCase()) ? { consecutiveRepeats: true } : null;
  }

  static mustContainVowels(control: AbstractControl): ValidationErrors | null {
    return !/[aeiouAEIOU]/.test(control.value) ? { noVowels: true } : null;
  }

  static mustContainVowelsIfLengthMoreThanTwo(control: AbstractControl): ValidationErrors | null {
    return control.value.length > 2 && !/[aeiouAEIOU]/.test(control.value)
      ? { noVowels: true }
      : null;
  }
}

export class LookupValidators {
  static passportAndAsylumValidation(
    countrySelection: string,
    number: string,
    passport: boolean
  ): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const countryControl = group.get(countrySelection);
      const numberControl = group.get(number);

      if (!countryControl || !numberControl) {
        return null; // If controls are missing, no validation
      }
      const country = countryControl.value;
      const idNumber = numberControl.value;

      if (country === 'ZA' && passport) {
        if (idNumber.length != 9) {
          numberControl.setErrors(null);
          numberControl.setErrors({ mustBeNineCharacters: true });
        }
      } else if (idNumber.length < 7) {
        numberControl.setErrors(null);
        numberControl.setErrors({ mustBeAtleastSevenCharacters: true });
      }

      if (RegExp(/^[a-zA-Z]+$/).test(idNumber)) {
        numberControl.setErrors(null);
        numberControl.setErrors({ noDigits: true });
      }

      if (!RegExp(/^[a-zA-Z0-9]+$/).test(idNumber)) {
        numberControl.setErrors(null);
        numberControl.setErrors({ specialCharacters: true });
      }

      return null;
    };
  }

  static southAfricanIdValidation(id: string): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const idControl = group.get(id);

      if (!idControl) {
        return null;
      }

      var info = saIdParser.parse(idControl.value) as {
        isValid: boolean;
        dateOfBirth: Date;
        isFemale: boolean;
        isSouthAfricanCitizen: boolean;
      };

      if (info.isValid) {
        if (!isAdult(info.dateOfBirth)) idControl.setErrors({ notAdult: true });

        return null;
      } else {
        idControl.setErrors({ notValid: true });
      }

      return null;
    };
  }

  // Validator to check if the person is 18 years or older
  static adultValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return { notAValidDate: true };
      }

      const date = new Date(control.value);
      if (isNaN(date.getTime())) {
        return { notAValidDate: true };
      }

      return isAdult(date) ? null : { notAdult: true };
    };
  }

  // Validator to check if the date is in the future
  static futureDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return { notAValidDate: true };
      }

      const date = new Date(control.value);
      if (isNaN(date.getTime())) {
        return { notAValidDate: true };
      }

      const currentDate = new Date();
      const selectedDate = new Date(control.value);
      return selectedDate > currentDate ? null : { futureDate: true };
    };
  }
}

export class InputValidators {
  static numbersOnlyValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const isValid = /^[0-9]*$/.test(control.value);
      return isValid ? null : { numbersOnly: { value: control.value } };
    };
  }

  static alphaNumericalInputOnlyValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const isValid = /^[a-zA-Z0-9]*$/.test(control.value);
      return isValid ? null : { alphaNumericalOnly: { value: control.value } };
    };
  }

  static alphabetInputOnlyValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value?.trimStart?.() ?? ''; // prevent crash if null

      const isValid = /^[A-Za-z]+(?: [A-Za-z]+)*$/.test(value);

      return isValid ? null : { alphabetOnly: { value: control.value } };
    };
  }

  static startsWithZeroValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;
      return value.startsWith('0') ? null : { numbersOnly: true };
    };
  }
}

function isAdult(dateOfBirth: Date): boolean {
  const currentDate = new Date();

  let age = currentDate.getFullYear() - dateOfBirth.getFullYear();
  const monthDifference = currentDate.getMonth() - dateOfBirth.getMonth();

  if (
    monthDifference < 0 ||
    (monthDifference === 0 && currentDate.getDate() < dateOfBirth.getDate())
  ) {
    age--;
  }

  return age >= 18;
}
